/**
 * Notification utility functions for role-based filtering and management
 */

import { AppNotification } from './Models';

export interface UserDetails {
  id: number;
  butcherId: number;
  roles: number[];
}

const normalizeRoleIds = (roles: unknown): number[] => {
  if (Array.isArray(roles)) {
    return roles
      .map((role) => {
        if (typeof role === 'number') return role;
        if (typeof role === 'string') {
          const parsed = Number(role);
          return Number.isNaN(parsed) ? null : parsed;
        }
        if (role && typeof role === 'object' && 'id' in role) {
          const roleId = (role as { id?: number | string }).id;
          const parsed = Number(roleId);
          return Number.isNaN(parsed) ? null : parsed;
        }
        return null;
      })
      .filter((roleId): roleId is number => roleId !== null);
  }

  if (typeof roles === 'string') {
    return roles
      .split(',')
      .map((role) => Number(role.trim()))
      .filter((roleId) => !Number.isNaN(roleId));
  }

  return [];
};

/**
 * Check if a notification should be shown to the current user based on their roles
 * @param notification The notification data received from the server
 * @param userRoles Array of role IDs the user has
 * @returns true if the notification should be shown, false otherwise
 */
export const shouldShowNotification = (
  notification: AppNotification,
  userRoles: unknown,
): boolean => {
  const targetRoleIds = normalizeRoleIds(notification.roleIds || []);
  const currentUserRoleIds = normalizeRoleIds(userRoles);

  // If no target roles specified, show to all users (fallback behavior)
  if (targetRoleIds.length === 0) {
    return true;
  }

  // If user roles are not available/parseable, fail open so socket notifications are not lost.
  if (currentUserRoleIds.length === 0) {
    return true;
  }

  // Check if user has any of the target roles
  return targetRoleIds.some((roleId: number) =>
    currentUserRoleIds.includes(roleId),
  );
};

/**
 * Get user details from localStorage
 * @returns UserDetails object or null if not found
 */
export const getUserDetails = (): UserDetails | null => {
  try {
    const userDetailsStr = localStorage.getItem('userDetails');
    if (!userDetailsStr) {
      return null;
    }
    return JSON.parse(userDetailsStr);
  } catch (error) {
    console.error('Error parsing user details from localStorage:', error);
    return null;
  }
};

/**
 * Filter notifications array based on user roles
 * @param notifications Array of notifications
 * @param userRoles Array of user role IDs
 * @returns Filtered array of notifications that should be shown to the user
 */
export const filterNotificationsByRole = (
  notifications: AppNotification[],
  userRoles: unknown,
): AppNotification[] => {
  return notifications.filter((notification) =>
    shouldShowNotification(notification, userRoles),
  );
};

/**
 * Log notification filtering for debugging
 * @param notification The notification being processed
 * @param userRoles User's role IDs
 * @param willShow Whether the notification will be shown
 */
export const logNotificationFiltering = (
  notification: AppNotification,
  userRoles: unknown,
  willShow: boolean,
): void => {
  const targetRoleIds = normalizeRoleIds(notification.roleIds || []);
  const normalizedUserRoles = normalizeRoleIds(userRoles);

  console.log(`Notification filtering:`, {
    // title: notification.title,
    targetRoleIds,
    userRoles: normalizedUserRoles,
    willShow,
    reason: willShow
      ? 'User has required role(s) or no role restriction'
      : 'User does not have required role(s)',
  });
};
